import React, { useContext, useState } from 'react';

import Modal from '../../utils/ModalComponet';
import '../../../styles/components/RequestRefundModal.css';
import { Order } from '../../../types/Order';
import { getBookingReference } from '../../../infrastructure/constants/orders';
import { getAmountToTransfer } from '../../order/helpers';
import { ActivityContext } from '../../../contexts/Activity';
import { ActivitySource } from '../../../types/Activity';
import {
  allowedToCancelOrder,
  isOrderCancelled,
} from '../../../infrastructure/utils/order';

import NeedFareharborOrderUpdatePage from './NeedFareharborOrderUpdate';
import NeedOrderCancelPage from './NeedOrderCancelPage';
import NeedOrderUpdatePage from './NeedOrderUpdatePage';
import ReasonChoicePage from './ReasonChoicePage';
import RequestRefundPage from './RequestRefundPage';
import SuccessMessagePage from './SuccessMessagePage';
import UpdateOrderAndRequestRefundPage from './UpdateOrderAndRequestRefundPage';

export type RequestRefundModalProps = {
  order: Order & { id: string };
  onClose(): void;
} & (
  | {
      order: Order & { id: string };
      variant: 'reasonChoice' | 'requestRefundOnly';
    }
  | {
      variant: 'updateOrderAndRequestRefund';
      refundAmount: number;
      reverseAmount: number;
      onOrderUpdate(reasonNote: string): Promise<void>;
    }
);

const variantToModalPage = {
  reasonChoice: 'reasonChoice',
  requestRefundOnly: 'requestRefund',
  updateOrderAndRequestRefund: 'updateOrderAndRequestRefund',
};

const RequestRefundModal = (props: RequestRefundModalProps) => {
  const activityContext = useContext(ActivityContext);
  const reverseAllowed = activityContext.source !== ActivitySource.bach;
  const allowEditOrderReason = isOrderCancelled(props.order) === false;

  const [modalPage, setModalPage] = useState<string>(
    variantToModalPage[props.variant]
  );
  const bookingReference = getBookingReference(props.order);

  const backToReasonChoice = () => setModalPage('reasonChoice');
  const showSuccessMessage = () => setModalPage('successMessage');

  return (
    <Modal
      open={true}
      closeModal={props.onClose}
      classNames={{ modal: 'request-refund-modal' }}
    >
      {modalPage === 'reasonChoice' && (
        <ReasonChoicePage
          allowCancelOrderReason={allowedToCancelOrder(props.order)}
          allowEditOrderReason={allowEditOrderReason}
          onReasonChoose={setModalPage}
          onCancel={props.onClose}
        />
      )}

      {modalPage === 'needOrderUpdate' && (
        <NeedOrderUpdatePage
          orderId={props.order.id}
          onBack={backToReasonChoice}
        />
      )}

      {modalPage === 'needFareharborOrderUpdate' && (
        <NeedFareharborOrderUpdatePage
          url={bookingReference?.url}
          onBack={backToReasonChoice}
        />
      )}

      {modalPage === 'needOrderCancel' && (
        <NeedOrderCancelPage
          orderId={props.order.id}
          onBack={backToReasonChoice}
        />
      )}

      {modalPage === 'requestRefund' && (
        <RequestRefundPage
          orderId={props.order.id}
          refund={{ limit: props.order.ledger.balance.totalAmount }}
          reverse={{
            limit: getAmountToTransfer(props.order.ledger.balance),
            allowed: reverseAllowed,
          }}
          onBack={backToReasonChoice}
          onSuccess={showSuccessMessage}
          showBackButton={props.variant === 'reasonChoice'}
        />
      )}

      {modalPage === 'updateOrderAndRequestRefund' &&
        props.variant === 'updateOrderAndRequestRefund' && (
          <UpdateOrderAndRequestRefundPage
            refund={{ amount: props.refundAmount }}
            reverse={{ amount: props.reverseAmount, allowed: reverseAllowed }}
            onOrderUpdate={props.onOrderUpdate}
            onClose={showSuccessMessage}
          />
        )}

      {modalPage === 'successMessage' && (
        <SuccessMessagePage onClose={props.onClose} />
      )}
    </Modal>
  );
};

export default RequestRefundModal;
